"use client";

import { useEffect, useRef, useCallback, RefObject } from "react";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useFiltersStore } from "@/stores/filtersStore";
import { useGlobalData } from "@/context/globalContext";
import { shouldLoadMore } from "@/lib/client/utils";

/**
 * Hook for image-based search functionality.
 *
 * Automatically refreshes search results when filters change (skipping initial
 * render). Provides scroll handler for infinite loading within a container.
 *
 * @returns Object containing:
 * - isActive: Whether image search is currently active
 * - loading: Whether initial search is loading
 * - loadingMore: Whether more items are being loaded
 * - items: Current search results
 * - handleScroll: Scroll event handler for infinite load (attach to container)
 *
 * @example
 * ```tsx
 * function ImageSearchResults() {
 *   const { isActive, items, loading, loadingMore, handleScroll } =
 *     useImageSearch();
 *
 *   if (!isActive) return null;
 *
 *   return (
 *     <div onScroll={handleScroll} style={{ overflowY: "auto", height: "500px" }}>
 *       {items.map(item => (
 *         <SearchResultCard key={item.id} {...item} />
 *       ))}
 *       {(loading || loadingMore) && <Spinner />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useImageSearch(scrollContainerRef?: RefObject<HTMLDivElement | null>) {
  const isActive = useImageSearchStore((state) => state.isActive);
  const loading = useImageSearchStore((state) => state.loading);
  const loadingMore = useImageSearchStore((state) => state.loadingMore);
  const items = useImageSearchStore((state) => state.items);
  const hasMore = useImageSearchStore((state) => state.hasMore);
  const sort = useImageSearchStore((state) => state.sort);
  const { refresh, loadMore } = useImageSearchStore((state) => state.actions);

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const excludeRelists = useFiltersStore((state) => state.excludeRelists);
  const excludeAuctions = useFiltersStore((state) => state.excludeAuctions);
  const daysBack = useFiltersStore((state) => state.daysBack);
  const { exchangeRates } = useGlobalData();
  const isFirstRender = useRef(true);
  const loadMoreRef = useRef(loadMore);
  const stateRef = useRef({ hasMore, loadingMore, loading });

  loadMoreRef.current = loadMore;
  stateRef.current = { hasMore, loadingMore, loading };

  // Use getState() not the closure: cross-store resets (resetFilters + resetImageSearch)
  // can land in separate React renders, so the closure may still see isActive=true here.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (useImageSearchStore.getState().isActive && !loading) {
      refresh(activeFilters, exchangeRates as Record<string, number> | undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, excludeRelists, excludeAuctions, daysBack, sort]);

  // Scroll handler for the inner container (desktop)
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loadingMore || loading) {
        return;
      }

      const target = e.currentTarget;
      const shouldLoad = shouldLoadMore({
        scrollTop: target.scrollTop,
        scrollHeight: target.scrollHeight,
        clientHeight: target.clientHeight,
        threshold: 0.6,
      });

      if (shouldLoad) {
        loadMore(activeFilters, exchangeRates as Record<string, number> | undefined);
      }
    },
    [hasMore, loadingMore, loading, loadMore, activeFilters, exchangeRates]
  );

  // On mobile the parent .searchPage is the scroll container, not .imageSearchContent.
  // Attach a scroll listener to the parent so infinite load still triggers.
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container || !isActive) return;

    const onScroll = () => {
      const { hasMore, loadingMore, loading } = stateRef.current;
      if (!hasMore || loadingMore || loading) return;

      const shouldLoad = shouldLoadMore({
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        threshold: 0.6,
      });

      if (shouldLoad) {
        loadMoreRef.current(activeFilters, exchangeRates as Record<string, number> | undefined);
      }
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [scrollContainerRef, isActive, activeFilters, exchangeRates]);

  // On small-device mobile the window is the scroll container (searchPage has height:auto).
  // Neither the onScroll on imageSearchContent nor the scrollContainerRef listener fires
  // in that case, so we need a window-level fallback. On desktop the window doesn't scroll
  // (layout is viewport-constrained), so this listener is effectively a no-op there.
  useEffect(() => {
    if (!isActive) return;

    const onWindowScroll = () => {
      const { hasMore, loadingMore, loading } = stateRef.current;
      if (!hasMore || loadingMore || loading) return;

      const shouldLoad = shouldLoadMore({
        scrollTop: window.scrollY,
        scrollHeight: document.documentElement.scrollHeight,
        clientHeight: window.innerHeight,
        threshold: 0.6,
      });

      if (shouldLoad) {
        loadMoreRef.current(activeFilters, exchangeRates as Record<string, number> | undefined);
      }
    };

    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, [isActive, activeFilters, exchangeRates]);

  return {
    isActive,
    loading,
    loadingMore,
    items,
    handleScroll,
  };
}
